import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Shield, ArrowRight, Sparkles, GraduationCap, Building2, ChevronLeft } from "lucide-react";
import AdvertisementBanner from "@/components/AdvertisementBanner";
import WhatsAppSupport from "@/components/WhatsAppSupport";
import Footer from "@/components/Footer";

const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";
const HERO_IMAGE_URL = "https://static.prod-images.emergentagent.com/jobs/42798270-bd37-4220-aae4-6680cf3b4cc3/images/544dc230bf95750ea0bc3604e207d45b6636e3ac5d938c255a2bb86eda2e89bd.png";

export default function RoleSelection() {
  const [step, setStep] = useState("main");
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();

  // Preload hero image for optimal performance
  useEffect(() => {
    const img = new Image();
    img.src = HERO_IMAGE_URL;
    img.onload = () => setImageLoaded(true);
  }, []);

  const handleAdminContinue = () => {
    navigate("/admin/login");
  };

  const handleRoleSelection = (role) => {
    navigate("/register", { state: { role } });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Announcement Bar */}
      <AdvertisementBanner placement="homepage" format="top_bar" />
      
      {/* Header */}
      <header className="px-4 py-3 bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg object-cover" />
            <span className="font-display text-lg font-bold text-gray-900">TalentMeet</span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-gray-600 hover:text-violet-600 text-sm hidden sm:inline-flex"
              onClick={() => navigate("/browse")}
              data-testid="browse-talents-btn"
            >
              Browse Talents
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-gray-600 hover:text-violet-600 text-sm hidden sm:inline-flex"
              onClick={() => navigate("/services")}
              data-testid="browse-services-btn"
            >
              Opportunities
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-violet-600 border-violet-200 hover:bg-violet-50"
              onClick={() => navigate("/login")}
              data-testid="login-btn"
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Clean, Premium, Cinematic */}
      <section 
        className={`relative flex-1 min-h-[calc(100vh-120px)] flex items-center transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        style={{
          backgroundImage: `url(${HERO_IMAGE_URL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          backgroundRepeat: 'no-repeat',
          filter: 'contrast(1.05) saturate(1.1)'
        }}
        data-testid="hero-section"
      >
        {/* Cinematic gradient overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, 
              rgba(255,255,255,0.7) 0%, 
              rgba(255,255,255,0.5) 30%, 
              rgba(255,255,255,0.25) 55%, 
              rgba(255,255,255,0.1) 75%, 
              transparent 100%)`
          }}
        />
        
        {/* Content Container */}
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-2xl">
            
            {/* Main Headline */}
            <div className="space-y-4 mb-8">
                <h1 
                  className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-tight"
                  style={{ 
                    color: '#1a1a2e',
                    textShadow: '0 2px 4px rgba(255,255,255,0.95)'
                  }}
                >
                  Where{" "}
                  <span 
                    className="bg-clip-text text-transparent"
                    style={{ 
                      backgroundImage: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #f472b6 100%)',
                      filter: 'saturate(1.3) brightness(1.1)'
                    }}
                  >
                    Talent
                  </span>{" "}
                  Meets{" "}
                  <span 
                    className="bg-clip-text text-transparent"
                    style={{ 
                      backgroundImage: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 50%, #7c3aed 100%)',
                      filter: 'saturate(1.3) brightness(1.1)'
                    }}
                  >
                    Opportunity
                  </span>
                </h1>
                <p 
                  className="text-base sm:text-lg lg:text-xl max-w-lg font-medium leading-relaxed"
                  style={{ color: '#374151', textShadow: '0 1px 3px rgba(255,255,255,0.7)' }}
                >
                  A unified platform connecting Talent, Trainers, and Enterprises in the entertainment industry.
                </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white px-8 py-6 text-base font-semibold shadow-lg shadow-violet-500/25"
                onClick={() => setStep("user_type")}
                data-testid="get-started-btn"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-gray-300 hover:border-violet-400 text-gray-700 hover:text-violet-600 px-8 py-6 text-base font-semibold bg-white/50 backdrop-blur-sm"
                onClick={() => navigate("/browse")}
                data-testid="learn-more-btn"
              >
                Explore Talents
              </Button>
            </div>

            {/* Admin Link */}
            <div>
              <button
                onClick={handleAdminContinue}
                className="text-sm text-gray-500 hover:text-teal-600 flex items-center gap-2 transition-colors"
                data-testid="admin-login-link"
              >
                <Shield className="w-4 h-4" />
                Admin Login
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Role Selection Modal */}
      {step === "user_type" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="role-selection-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Choose Your Role</h2>
                <button 
                  onClick={() => setStep("main")}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="close-role-modal"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3" data-testid="user-type-selection">
                {/* Talent */}
                <Card 
                  className="p-4 border-2 border-transparent hover:border-violet-200 cursor-pointer transition-all bg-white shadow-sm hover:shadow-md"
                  onClick={() => handleRoleSelection("talent")}
                  data-testid="role-talent-btn"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">Talent</h3>
                        <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Free to start</span>
                      </div>
                      <p className="text-sm text-gray-500">Actor, Model, Singer, Dancer</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Card>

                {/* Trainer */}
                <Card 
                  className="p-4 border-2 border-transparent hover:border-orange-200 cursor-pointer transition-all bg-white shadow-sm hover:shadow-md"
                  onClick={() => handleRoleSelection("trainer")}
                  data-testid="role-trainer-btn"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">Trainer</h3>
                        <span className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">14-day free trial</span>
                      </div>
                      <p className="text-sm text-gray-500">Acting coach, Voice trainer</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Card>

                {/* Enterprise */}
                <Card 
                  className="p-4 border-2 border-transparent hover:border-blue-200 cursor-pointer transition-all bg-white shadow-sm hover:shadow-md"
                  onClick={() => handleRoleSelection("enterprise")}
                  data-testid="role-enterprise-btn"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">Enterprise</h3>
                        <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">14-day free trial</span>
                      </div>
                      <p className="text-sm text-gray-500">Production house, Agency</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Card>

                {/* Sign in link */}
                <div className="text-center pt-4 border-t mt-4">
                  <p className="text-sm text-gray-600">
                    Already have an account?{" "}
                    <button 
                      onClick={() => navigate("/login")}
                      className="text-violet-600 hover:text-violet-700 font-medium"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer variant="full" />
      
      {/* WhatsApp Support */}
      <WhatsAppSupport />
    </div>
  );
}
